import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, INQUIRY_STATUS_LABEL, INQUIRY_SOURCE_LABEL, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { WebsiteCalculationCard } from "@/components/WebsiteCalculationCard";
import { FileImportStatus } from "@/components/FileImportStatus";

export default function AnfrageDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [item, setItem] = useState(null);
  const [notiz, setNotiz] = useState("");
  const [status, setStatus] = useState("");
  const [dialog, setDialog] = useState(false);
  const [mode, setMode] = useState("neu");
  const [kunden, setKunden] = useState([]);
  const [kundeId, setKundeId] = useState("");
  const [titel, setTitel] = useState("");
  const [saving, setSaving] = useState(false);
  const [vorschlag, setVorschlag] = useState(null);

  const load = async () => {
    try {
      const r = await api.get(`/anfragen/${id}`);
      setItem(r.data);
      setNotiz(r.data.notiz_intern || "");
      setStatus(r.data.status);
      setTitel((r.data.nachricht || "").slice(0, 60));
    } catch { toast.error("Anfrage nicht gefunden."); nav("/anfragen"); }
  };
  useEffect(() => { load();  }, [id]);

  const saveMeta = async () => {
    try {
      const r = await api.patch(`/anfragen/${id}`, { status, notiz_intern: notiz });
      setItem(r.data);
      toast.success("Anfrage aktualisiert.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehlgeschlagen."); }
  };

  const openConvert = async () => {
    const [r, s] = await Promise.all([api.get("/kunden"), api.get(`/anfragen/${id}/umwandeln-vorschlag`).catch(() => ({ data: {} }))]);
    setKunden(r.data);
    setVorschlag(s.data);
    const dup = s.data?.duplikate?.[0];
    setMode(dup ? "existing" : "neu");
    setKundeId(dup?.id || r.data[0]?.id || "");
    setDialog(true);
  };

  const convert = async () => {
    setSaving(true);
    try {
      const body = { titel };
      if (mode === "existing") body.kunde_id = kundeId;
      if (mode === "neu" && vorschlag?.kunde) body.neuer_kunde = vorschlag.kunde;
      if (vorschlag?.position) body.positionen = [vorschlag.position];
      const r = await api.post(`/anfragen/${id}/umwandeln`, body);
      toast.success(`Auftrag ${r.data.auftragsnummer} erstellt.`);
      nav(`/auftraege/${r.data.order_id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Umwandlung fehlgeschlagen."); }
    finally { setSaving(false); }
  };

  if (!item) return <div className="text-slate-500">Lade…</div>;
  const isConverted = item.status === "in_auftrag_umgewandelt";

  return (
    <div className="space-y-6" data-testid="anfrage-detail">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <Button variant="ghost" size="sm" onClick={() => nav("/anfragen")} className="mb-2 -ml-2"><ArrowLeft className="w-4 h-4 mr-1" /> Zurück</Button>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="font-mono text-xs text-slate-500">{item.anfrage_nummer}</span>
            <Badge variant="outline">{INQUIRY_SOURCE_LABEL[item.quelle]}</Badge>
            <Badge className="bg-blue-50 text-blue-700 border-blue-200">{INQUIRY_STATUS_LABEL[item.status]}</Badge>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mt-2">{item.name || item.email || "Anfrage"}</h1>
          <div className="text-sm text-slate-500 mt-1">Eingegangen {fmtDateTime(item.created_at)}</div>
        </div>
        {!isConverted && (
          <Button onClick={openConvert} data-testid="anfrage-convert-button">
            <ArrowRight className="w-4 h-4 mr-2" /> In Auftrag umwandeln
          </Button>
        )}
        {isConverted && item.converted_order_id && item.converted_order_number && (
          <Button variant="outline" onClick={() => nav(`/auftraege/${item.converted_order_id}`)} data-testid="anfrage-goto-auftrag">
            Auftrag {item.converted_order_number} →
          </Button>
        )}
        {isConverted && item.converted_order_id && !item.converted_order_number && (
          <Button variant="outline" onClick={() => nav(`/auftraege/${item.converted_order_id}`)} data-testid="anfrage-goto-auftrag">
            Zum Auftrag →
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <Card className="border-slate-200">
            <CardContent className="p-5 space-y-3">
              <h2 className="text-base font-semibold">Nachricht</h2>
              <div className="text-sm text-slate-700 whitespace-pre-wrap bg-slate-50 p-3 rounded border border-slate-200" data-testid="anfrage-nachricht">{item.nachricht}</div>
              {item.anhaenge?.length > 0 && (
                <div>
                  <div className="text-xs uppercase font-mono tracking-wider text-slate-500 mb-1">Anhänge</div>
                  <ul className="text-sm list-disc pl-5">
                    {item.anhaenge.map((a, i) => (
                      <li key={i} data-testid={`anfrage-anhang-${i}`}>
                        {a.url ? <a href={a.url} className="text-blue-600 hover:underline" target="_blank" rel="noreferrer">{a.filename}</a> : a.filename}
                        {a.quelle && <span className="text-xs text-slate-400"> · {a.quelle}</span>}
                        {a.hinweis && <span className="text-xs text-amber-700"> – {a.hinweis}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {item.anhaenge?.some((a) => a.url) && (
                <FileImportStatus state={item.datei_import} link={item.anhaenge.find((a) => a.url).url} endpoint={`/anfragen/${id}/datei-import`} canImport={isConverted} onChange={load} />
              )}
              {item.quelle === "website" && !item.anhaenge?.length && (
                <div className="text-xs text-amber-700" data-testid="anfrage-no-file-hint">Keine Kundendatei übermittelt – bitte klären.</div>
              )}
            </CardContent>
          </Card>
          <WebsiteCalculationCard item={item} />
          <Card className="border-slate-200">
            <CardContent className="p-5 space-y-3">
              <h2 className="text-base font-semibold">Interne Notiz</h2>
              <Textarea rows={4} value={notiz} onChange={(e) => setNotiz(e.target.value)} data-testid="anfrage-notiz-input" />
              <div className="flex items-center gap-3">
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="w-56" data-testid="anfrage-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(INQUIRY_STATUS_LABEL).filter(([k]) => k !== "in_auftrag_umgewandelt").map(([k, l]) => (
                      <SelectItem key={k} value={k}>{l}</SelectItem>
                    ))}
                    {isConverted && <SelectItem value="in_auftrag_umgewandelt">{INQUIRY_STATUS_LABEL.in_auftrag_umgewandelt}</SelectItem>}
                  </SelectContent>
                </Select>
                <Button onClick={saveMeta} data-testid="anfrage-save-meta-button">Speichern</Button>
              </div>
            </CardContent>
          </Card>
        </div>
        <div className="space-y-4">
          <Card className="border-slate-200">
            <CardContent className="p-5 space-y-3 text-sm">
              <h2 className="text-base font-semibold mb-2">Kontakt</h2>
              <div><div className="text-xs text-slate-500 uppercase tracking-wider font-mono">Name</div>{item.name || "—"}</div>
              <div><div className="text-xs text-slate-500 uppercase tracking-wider font-mono">Firma</div>{item.firma || "—"}</div>
              <div><div className="text-xs text-slate-500 uppercase tracking-wider font-mono">E-Mail</div>{item.email || "—"}</div>
              <div><div className="text-xs text-slate-500 uppercase tracking-wider font-mono">Telefon</div>{item.telefon || "—"}</div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent data-testid="convert-dialog">
          <DialogHeader><DialogTitle>Anfrage in Auftrag umwandeln</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <RadioGroup value={mode} onValueChange={setMode} className="grid grid-cols-2 gap-3">
              <label className="flex items-start gap-2 p-3 border rounded-md cursor-pointer hover:bg-slate-50" data-testid="convert-mode-new">
                <RadioGroupItem value="neu" className="mt-1" />
                <span><b>Neuen Kunden anlegen</b><br /><span className="text-xs text-slate-500">Aus Anfrage-Daten vorausgefüllt</span></span>
              </label>
              <label className="flex items-start gap-2 p-3 border rounded-md cursor-pointer hover:bg-slate-50" data-testid="convert-mode-existing">
                <RadioGroupItem value="existing" className="mt-1" />
                <span><b>Bestehenden Kunden wählen</b></span>
              </label>
            </RadioGroup>
            {mode === "existing" && (
              <div>
                <Label>Kunde</Label>
                <Select value={kundeId} onValueChange={setKundeId}>
                  <SelectTrigger data-testid="convert-kunde-select"><SelectValue placeholder="Kunde wählen" /></SelectTrigger>
                  <SelectContent>
                    {kunden.map((k) => <SelectItem key={k.id} value={k.id}>{k.kundennummer} — {k.firma || `${k.vorname} ${k.nachname}`.trim()}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div><Label>Auftrags-Titel</Label><Input value={titel} onChange={(e) => setTitel(e.target.value)} data-testid="convert-titel-input" /></div>
            {vorschlag?.duplikate?.length > 0 && (
              <p className="text-xs text-amber-700" data-testid="convert-duplicate-hint">Es gibt bereits {vorschlag.duplikate.length} Kunden mit dieser E-Mail ({vorschlag.duplikate.map((d) => d.kundennummer).join(", ")}).</p>
            )}
            {vorschlag?.position && (
              <div className="text-xs bg-slate-50 border border-slate-200 rounded p-2" data-testid="convert-position-preview">
                Position wird übernommen: <b>{vorschlag.position.beschreibung}</b> · {vorschlag.position.menge} × {Number(vorschlag.position.einzelpreis_netto).toLocaleString("de-DE", { style: "currency", currency: "EUR" })} netto · {vorschlag.position.mwst_satz} % MwSt.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Abbrechen</Button>
            <Button onClick={convert} disabled={saving} data-testid="convert-confirm-button">Auftrag erstellen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
